import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { useDebugMode } from '../../hooks/useDebugMode';
import { AdminStats } from '@curbside-quest/shared';
import { PendingReportsQueue } from './PendingReportsQueue';
import { UserManagement } from './UserManagement';
import { AuditLog } from './AuditLog';
import { AdminStats as AdminStatsComponent } from './AdminStats';

interface AdminDashboardProps {}

export const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const { user, isAdmin, role, customClaims } = useAuth();
  const isDebugMode = useDebugMode();
  const [activeTab, setActiveTab] = useState<'reports' | 'users' | 'stats' | 'audit'>('reports');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Memoize Firebase Functions - same pattern as useReports
  const getAdminStatsFunction = useMemo(() => httpsCallable(functions, 'getAdminStats'), []);
  const syncUserRoleFunction = useMemo(() => httpsCallable(functions, 'syncUserRole'), []);

  useEffect(() => {
    if (!isAdmin) return;
    
    // Debug: Check current user token claims
    if (user) {
      user.getIdTokenResult().then(idTokenResult => {
        console.log('Current user token claims:', idTokenResult.claims);
        console.log('Is admin:', !!idTokenResult.claims.admin);
        console.log('Is moderator:', !!idTokenResult.claims.moderator);
      });
    }
    
    // Sync user role with claims
    syncUserRole();
    loadAdminStats();
  }, [isAdmin, user]);

  const syncUserRole = async () => {
    try {
      await syncUserRoleFunction();
      console.log('User role synced successfully');
    } catch (error) {
      console.error('Error syncing user role:', error);
    }
  };

  const loadAdminStats = async () => {
    try {
      setLoading(true);
      const result = await getAdminStatsFunction();
      const data = result.data as AdminStats;
      setStats(data);
    } catch (error) {
      console.error('Error loading admin stats:', error);
      // Fallback to empty stats on error
      setStats({
        reportCounts: {
          pending: 0,
          active: 0,
          going_soon: 0,
          rejected: 0,
          gone: 0
        },
        userCount: 0,
        recentActivity: {
          reportsLastWeek: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-sky-neutral flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-urban mb-4">Access Denied</h1>
          <p className="text-urban-300">You don't have permission to access the admin dashboard.</p>
          {user && (
            <div className="mt-4 space-y-2">
              <button
                onClick={async () => {
                  try {
                    console.log('Forcing token refresh...');
                    await user.getIdToken(true); // Force refresh
                    await syncUserRole(); // Sync role with claims
                    window.location.reload(); // Reload to pick up new claims
                  } catch (error) {
                    console.error('Error refreshing token:', error);
                  }
                }}
                className="bg-cardboard hover:bg-cardboard-600 text-white px-4 py-2 rounded-md"
              >
                Refresh Token & Reload
              </button>
              <p className="text-sm text-urban-300">
                If you were just made a moderator, click this button to refresh your permissions.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-neutral">
      <div className="bg-teddy shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-urban">Admin Dashboard</h1>
              <p className="text-urban-300">Manage reports, users, and platform content</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        {loading ? (
          <div className="bg-teddy rounded-lg shadow-card p-6 mb-8">
            <div className="animate-pulse text-urban-300">Loading admin stats...</div>
          </div>
        ) : (
          stats && <AdminStatsComponent stats={stats} />
        )}

        {/* Navigation Tabs */}
        <div className="bg-teddy rounded-lg shadow-card mb-8">
          <div className="border-b border-urban-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { key: 'reports', label: 'Reports', count: stats?.reportCounts.pending },
                { key: 'users', label: 'User Management' },
                { key: 'stats', label: 'Statistics' },
                { key: 'audit', label: 'Audit Log' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-cardboard text-cardboard'
                      : 'border-transparent text-urban-300 hover:text-urban hover:border-urban-300'
                  }`}
                >
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="ml-2 bg-treasure text-urban py-0.5 px-2.5 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'reports' && <PendingReportsQueue />}
            {activeTab === 'users' && <UserManagement />}
            {activeTab === 'stats' && stats && <AdminStatsComponent stats={stats} detailed />}
            {activeTab === 'audit' && <AuditLog />}
          </div>
        </div>
      </div>

      {/* Debug Info - Only shown when ?debug=true */}
      {isDebugMode && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="bg-sky-neutral border border-urban-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-2 text-urban">Debug Info</h3>
            <div className="text-sm text-urban-300 space-y-1">
              <div>User: {user ? user.email : 'Not logged in'}</div>
              <div>Role: {role || 'visitor'}</div>
              <div>Claims: {JSON.stringify(customClaims)}</div>
              <div>isAdmin: {isAdmin ? 'Yes' : 'No'}</div>
              <div>Loading: {loading ? 'Yes' : 'No'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};